import { useState, useEffect } from "react";
import { useUser } from "../contexts/UserContext";
import ConfirmationModal from "./ConfirmationModal";
import { checkPrinterConnection, getLocalNetworkPermissionStatus } from "../api/services/devices/printer";

const PRINTER_STORAGE_KEY = "kindmore_printer_permission_granted";

/**
 * PrinterConnectionModal Component
 * 
 * Automatically checks for printer service availability and local network permissions.
 * If either is missing, it shows a modal to prompt the user to connect.
 */
export default function PrinterConnectionModal() {
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [status, setStatus] = useState({
    permission: "unknown",
    isConnected: true,
  });
  const isProduction = import.meta.env.MODE === "production";
  const verifyConnection = async (isManualTest = false) => {
    const permission = await getLocalNetworkPermissionStatus();
    const connected = await checkPrinterConnection();

    setStatus({ permission, isConnected: connected });

    const hasIssue = permission === "prompt" || permission === "denied" || !connected;

    if (hasIssue) {
      console.warn("[PrinterConnectionModal] skip showing modal", { permission, isConnected: connected });
      // setIsOpen(true);
    } else if (isManualTest) {
      setIsOpen(false);
      localStorage.setItem(PRINTER_STORAGE_KEY, "true");
    }
    setIsChecking(false);
  };

  useEffect(() => {
    const handleTrigger = () => {
      console.warn("[PrinterConnectionModal] skip showing modal from printer-connection-error");
      setIsChecking(false);
      // setIsOpen(true);
      verifyConnection(false);
    };

    window.addEventListener("printer-connection-error", handleTrigger);

    if (!isProduction) {
      setIsChecking(false);
    } else {
      verifyConnection();
    }

    return () => window.removeEventListener("printer-connection-error", handleTrigger);
  }, [isProduction]);

  const handleConnect = async () => {
    await verifyConnection(true);
  };

  const getDescription = () => {
    if (status.permission === "denied" || status.permission === "prompt") {
      return "โปรดตรวจสอบสิทธิ์ \"Private Network\" ในการตั้งค่าเบราว์เซอร์";
    }

    if (!status.isConnected) {
      return "โปรดตรวจสอบว่าเปิดโปรแกรม \"Kindmore Agent\" แล้ว";
    }

    return [
      "1. โปรดตรวจสอบสิทธิ์ \"Local App\" ในการตั้งค่าเบราว์เซอร์",
      "2. โปรดตรวจสอบว่าเครื่องพิมพ์เปิดอยู่และเชื่อมต่อถูกต้อง",
      "3. หากยังคงพบปัญหา กรุณาติดต่อเจ้าหน้าที่ (CS)",
    ].join("\n");
  };

  const allowedRoles = ["เจ้าหน้าที่คลินิก", "ผู้จัดการคลินิก", "พยาบาล", "แพทย์"];
  const isAllowedRole = user && allowedRoles.includes(user.role);

  if (!isAllowedRole || isChecking || !isOpen) return null;

  return (
    <ConfirmationModal
      title="ไม่สามารถเชื่อมต่อเครื่องพิมพ์"
      description={getDescription()}
      isOpen={isOpen}
      onCancel={() => setIsOpen(false)}
      onConfirm={handleConnect}
      confirmButtonText="ทดสอบการเชื่อมต่อ"
      cancelButtonText="ปิด"
      iconName="x-circle"
      iconColor="text-main-icon-accented-error"
      iconBackgroundColor="bg-main-background-accented-error"
      descriptionAlignment="start"
    />
  );
}
